import { TemplateWatermarkProperties } from '../../template/types';
import { Config } from '../../../video-data/config';

import type {
    CommonAttributeSettings,
    PropDescriptor
} from '../common';
import {
    DEFAULT_ATTRIBUTE_SETTINGS,
    defineMetadata
} from '../common';
import {
    createTemplateGetter,
    createTemplateSetter,
    createThemeRef,
    isTemplateReference,
    ThemeRefTypes
} from './common';

type WatermarkAttributeSettings = CommonAttributeSettings & {
     supportsInherit: boolean;
};

export const WATERMARK_ATTRIBUTE_TYPE = 'watermarkAttributeType';

const DEFAULT_SETTINGS: WatermarkAttributeSettings = {
    ...DEFAULT_ATTRIBUTE_SETTINGS,
    supportsInherit: false,
};

function createValidator(supportsInherit: boolean) {
    return function watermarkValueValidator(value: any): void {
        if (supportsInherit && value === Config.INHERIT_FROM_TEMPLATE) {
            return;
        }
        if (value === Config.WATERMARK_NONE) {
            return;
        }
        if (!isTemplateReference(value)) {
            throw Error(`Invalid value for watermark attribute: ${value}`);
        }
        if (!value.includes(ThemeRefTypes.WATERMARK_SOURCES)) {
            throw Error(`Invalid value for watermark theme ref: ${value}`);
        }
    };
}

export function templateWatermarkSourcesByIndex(index: number): string {
    return createThemeRef(ThemeRefTypes.WATERMARK_SOURCES, index);
}

/**
 * Creates an attribute decorator used to describe a watermark property. This will allow setting
 * either a null value or a watermark theme reference string such as themeRef_watermarks_0. This
 * requires the object to have a reference to this.template, which must contain a list of
 * watermarks (e.g. this.template.watermarks)
 *
 * If supportsInherit is enable, it means a value of "inherit from template", is supported and
 * it will look up the value on the object's template to inherit values.
 */
export function WatermarkAttribute(settings: Partial<WatermarkAttributeSettings> = {}) {
    settings = { ...DEFAULT_SETTINGS, ...settings };
    return function(target: object, propertyKey: string, descriptor: PropDescriptor) {
        const initialValue = descriptor.initializer();
        const isInitialValueOverridden = new WeakMap();

        defineMetadata(target, propertyKey, settings, WATERMARK_ATTRIBUTE_TYPE);

        let inheritKey;
        if (settings.supportsInherit) {
            inheritKey = Config.WATERMARK_INHERIT_TEMPLATE_KEY;
        }

        return {
            configurable: true,
            enumerable: true,
            get: createTemplateGetter(propertyKey, initialValue, ThemeRefTypes.WATERMARK_SOURCES, TemplateWatermarkProperties.SOURCE_DATA, inheritKey),
            set: createTemplateSetter(propertyKey, initialValue, createValidator(settings.supportsInherit), isInitialValueOverridden, ThemeRefTypes.WATERMARK_SOURCES),
        };
    } as any;
}
